import React, { useState, useRef } from 'react';
import { FileText, Eye, Upload, Loader2, AlertCircle } from 'lucide-react';
import { CrlvFile } from '../../types/crlv';
import { CrlvViewerModal } from './CrlvViewerModal';
import { storageService } from '../../services/storageService';

interface CrlvFieldInputProps {
  value: string;
  onChange: (val: string) => void;
  required?: boolean;
}

export function CrlvFieldInput({ value, onChange, required }: CrlvFieldInputProps) {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedFileForModal, setSelectedFileForModal] = useState<CrlvFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build a CrlvFile object for previewing
  const getCrlvFileForPreview = (pathOrValue: string): CrlvFile => {
    let normalizedPath = pathOrValue;
    if (!pathOrValue.startsWith('http') && !pathOrValue.startsWith('data:') && !pathOrValue.startsWith('file:')) {
      if (pathOrValue.startsWith('/api/crlv/file/')) {
        normalizedPath = pathOrValue;
      } else {
        const cleaned = pathOrValue.replace(/^\/?(CRLV|crlv)\/?/i, '');
        normalizedPath = `/api/crlv/file/CRLV/${cleaned}`;
      }
    }
    const rawFilename = pathOrValue.split('/').pop() || pathOrValue;
    let filename = rawFilename;
    try {
      filename = decodeURIComponent(rawFilename).replace(/\+/g, ' ');
    } catch {
      filename = rawFilename.replace(/\+/g, ' ');
    }
    const ext = filename.split('.').pop()?.toLowerCase() || 'pdf';

    return {
      id: pathOrValue,
      folder: 'crlv',
      name: filename,
      relativePath: normalizedPath,
      extension: ext,
    };
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const { publicUrl } = await storageService.uploadFile(file, 'crlv');
      onChange(publicUrl);
    } catch (err: any) {
      console.error('Error in handleFileUpload:', err);
      alert(`Erro ao realizar o upload do CRLV: ${err.message || err}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenPreview = () => {
    if (!value) return;
    const crlvObj = getCrlvFileForPreview(value);
    setSelectedFileForModal(crlvObj);
    setIsViewerOpen(true);
  };

  const isLocalFilePath = value.toLowerCase().startsWith('file:') || /^[a-zA-Z]:[\\/]/.test(value);
  const isPdf = value.toLowerCase().endsWith('.pdf') || value.toLowerCase().includes('pdf');

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">
        Documento CRLV {required && <span className="text-red-500">*</span>}
      </label>

      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {/* Simple input field representing the direct DB path or public URL */}
          <input
            type="text"
            required={required}
            className="block w-full rounded-lg border border-slate-300 p-2 text-xs sm:text-sm shadow-xs focus:border-red-500 focus:ring-red-500 font-mono"
            placeholder="Ex: https://.../object/public/CRLV/documento.pdf"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileUpload}
            className="hidden"
          />

          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 rounded-lg shadow-xs transition-colors cursor-pointer shrink-0"
            title="Enviar arquivo para o bucket CRLV"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Enviar CRLV
              </>
            )}
          </button>
        </div>

        {isLocalFilePath && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <span className="font-bold">Aviso:</span> Este registro aponta para um arquivo local do computador. Para torná-lo visível em todos os computadores e celulares, clique no botão <strong>Enviar CRLV</strong> acima.
            </div>
          </div>
        )}

        {/* Document attachment preview & viewer */}
        {value ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-2.5 gap-2">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className={`p-1.5 rounded-md shrink-0 ${isPdf ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                <FileText className="h-5 w-5" />
              </div>
              <div className="overflow-hidden min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate" title={value}>
                  {value.split('/').pop() || value}
                </p>
                <p className="text-[10px] text-slate-500 font-mono truncate">
                  {value}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
              <button
                type="button"
                onClick={handleOpenPreview}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-md shadow-xs transition-colors cursor-pointer"
              >
                <Eye className="h-3.5 w-3.5 text-red-400" />
                Visualizar Arquivo
              </button>
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 italic">
            Nenhum documento CRLV associado a este veículo no momento.
          </div>
        )}
      </div>

      {/* CRLV Viewer Modal */}
      <CrlvViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        crlvFile={selectedFileForModal}
      />
    </div>
  );
}
